import { ui } from '@/constants';
import { isGroupBYO, isMenuItemRewardsExclusive, slugify } from '@/helpers';
import { MBV2CachedHoverEntree, Menu, MenuItemGroup, MenuMetadataForChannel } from '@/models';
import { Routes } from '@/router';
import { NutritionLinkClickEvent } from '@/services/analytics/events';
import { NutritionLinkClickType } from '@/services/analytics/platforms/adobe-analytics/models';
import debounce from 'lodash/debounce';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

@Component({
  name: 'CmgNutritionCalculatorEntreeSelector',
})
export default class CmgNutritionCalculatorEntreeSelector extends Vue {
  menuItemSelectorImageUrl = this.mealSelectorUrl.default;
  isHoveringMealName = false;
  hoveredEntrees: MBV2CachedHoverEntree[] = [];

  get mealSelectorUrl() {
    return this.$appStore.state.cms.images.nutritionCalculator.mealSelector;
  }

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get menuMetadata(): MenuMetadataForChannel | null {
    return this.$appStore.state.menu.metadata;
  }

  get groups(): MenuItemGroup[] {
    if (!this.menu || !this.menuMetadata) {
      return [];
    }

    const isEnrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile);
    // Get all valid menu groups. Don't show BYO group in the selector
    let validMenuGroups =  this.$services.metadata.getValidMenuGroupsForMenu(this.menu, this.menuMetadata, true)?.filter(g => !isGroupBYO(g)) ?? [];
    
    if (!isEnrolledInLoyalty) {
      validMenuGroups = validMenuGroups.filter(g => !isMenuItemRewardsExclusive(g.tags));
    }

    // Not all valid menu groups are valid for NC (i.e. drinks on universal menu has no valid items in its subsection)
    return this.$services.monoMenu.getValidNutritionGroups(validMenuGroups);
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.tablet;
  }

  async mounted() {
    await this.$services.monoMenu.awaitCacheItemsAndBuildMenu();
    await this.$services.config.setLegalText();

    if (!this.$appStore.state.monoMenu.resizeListener) {
      const resizeListener = debounce(() => {
        this.$services.monoMenu.setMetadataCache();
      }, 500);
      this.$services.monoMenu.setMonoMenuResizeListener(resizeListener);

      window.addEventListener('resize', resizeListener);
    }
  }

  async onItemGroupClick(group: MenuItemGroup) {
    await this.$services.monoMenu.awaitCacheItemsAndBuildMenu();

    const openTopLevelMenuResult = this.$services.mealBuilderV2.openEmptyTopLevelMenuItem(group.displayName);
    this.$router.push({ name: Routes.NutritionCalculcatorMealBuilder, params: { topLevelName: slugify(group.displayName) } });

    openTopLevelMenuResult.finally(() => this.$services.analytics.send(new NutritionLinkClickEvent(NutritionLinkClickType.SelectYourMeal)));
  }

  onMouseOverMealName(group: MenuItemGroup) {
    this.isHoveringMealName = true;
    this.menuItemSelectorImageUrl = group.thumbnailImageUrl;
    if(!this.hoveredEntrees.some( h => h.name === group.displayName)) {
      this.$services.mealBuilderV2.openEmptyTopLevelMenuItem(group.displayName);
      this.hoveredEntrees.push({
        name: group.displayName,
        nutrition: this.$appStore.state.mealBuilderV2.nutrition
      });
    }
    this.$emit('entree-hover', this.hoveredEntrees.filter(h => h.name === group.displayName)[0]);
  }

  onMouseLeaveMealName(group: MenuItemGroup) {
    this.isHoveringMealName = false;
    this.menuItemSelectorImageUrl = this.mealSelectorUrl.default;
    this.$emit('entree-hover');
  }

  titleToClassName(name: string) {
    return slugify(name);
  }
}
